import * as yup from "yup";
import { useState, useEffect, useContext } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import { Helmet } from "react-helmet-async";
import { post } from "../../utils/request";
import {
  prependHTTPsForSocialLinks,
  handleSetErrorAndSwitchFormState,
  uploadImageToS3,
  validationForImage,
  validationForURLPattern,
} from "../../utils/HelperFunctions";
import { BRAND_ROUTE, ROUTES, TOAST_CONFIG } from "../../constants";
import BrandOnboarding from "./BrandOnboarding";
import { UserContext } from "../../components/context/UserContext";
import NotFound from "../NotFound";

type FormFieldsType =
  | "website_link"
  | "instagram_link"
  | "twitter_link"
  | "other_link"
  | "profile_image_url"
  | "cover_image_url"
  | "country"
  | "state"
  | "city"
  | "zipcode"
  | "name"
  | "about";

// field should be in reverse order
const formFields = {
  website_link: "2",
  instagram_link: "2",
  twitter_link: "2",
  other_link: "2",
  profile_image_url: "2",
  cover_image_url: "2",
  country: "1",
  state: "1",
  city: "1",
  zipcode: "1",
  name: "0",
  about: "0",
};

const schema = [
  yup.object().shape({
    name: yup.string().trim().required("* Required field"),
    about: yup.string().trim().required("* Required field"),
  }),
  yup.object().shape({
    country: yup.string().trim().required("Please Select Country"),
    state: yup.string().trim().required("Please Select State"),
    city: yup.string().trim().required("Please Enter City"),
    zipcode: yup
      .string()
      .trim()
      .required("Please Enter Zip Code")
      .max(15, "Zip Code Cannot Be Longer Than 15 Characters"),
  }),
  yup.object().shape({
    profile_image: validationForImage,
    cover_image: validationForImage,
    website_link: validationForURLPattern("website_link", false).required("* Required"),
    instagram_link: validationForURLPattern("instagram_link", false),
    twitter_link: validationForURLPattern("twitter_link", false),
    other_link: validationForURLPattern("other_link", false),
  }),
];

const createBrandDefaultValues = {
  name: "",
  about: "",
  country: "United states",
  state: undefined,
  city: undefined,
  zipcode: undefined,
  website_link: undefined,
  instagram_link: undefined,
  twitter_link: undefined,
  other_link: undefined,
  profile_image: undefined,
  cover_image: undefined,
};

function BrandOnBoardingPage() {
  const { reloadUserData } = useContext(UserContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const stepFromUrl = searchParams.get("step");

  const currentStep: number = Number(stepFromUrl);

  const formMethods = useForm({
    resolver: yupResolver(schema[currentStep]),
    defaultValues: createBrandDefaultValues,
  });

  const { handleSubmit, setError, getValues } = formMethods;

  const navigate = useNavigate();

  const handleSetFormStep = (step: number | string, replace?: boolean) => {
    searchParams.set("step", step.toString());
    setSearchParams(searchParams, {
      replace,
    });
  };

  // This useEffect prevents the user from skipping any form steps by manually changing the URL>
  useEffect(() => {
    // If no step in url, explicity set step to zero with search params.
    if (stepFromUrl === null) {
      handleSetFormStep(0, true);
      return;
    }
    // Bring back user to step 0 if when user manually enters the url in address bar, before filling in required fields.
    if (currentStep > 0 && (!getValues("name") || !getValues("about"))) {
      handleSetFormStep(0, true);
      return;
    }
    // Bring back user to step 1 if when user manually enters the url in address bar, before filling in required fields.
    if (currentStep > 1 && (!getValues("state") || !getValues("city") || !getValues("zipcode"))) {
      handleSetFormStep(1, true);
    }
  }, [currentStep]);

  // TODO: Render not found page in case of invalid value in form step as derived from URL.
  if (currentStep > 3 || currentStep < 0) {
    return <NotFound />;
  }

  const handleBrandCreateSuccess = () => {
    setLoading(false);
    reloadUserData();
    navigate(ROUTES.BRANDS_SIGNUP_SUCCESS, {
      state: {
        reload: "true",
      },
    });
  };

  const handleBrandCreateError = (errorMessage: string, error: any, errorData: any) => {
    toast.error("Error Creating Brand ", TOAST_CONFIG);
    const step = handleSetErrorAndSwitchFormState<FormFieldsType>(errorData, formFields, setError);
    if (step) handleSetFormStep(step);
    setLoading(false);
  };

  const handlePostDataToServer = async () => {
    const formValues = getValues();
    const formValuesWithHTTPSAppendedForSocialLinks = prependHTTPsForSocialLinks(formValues);
    setLoading(true);

    // Upload Images to S3
    const formImages = [formValues.profile_image?.[0], formValues.cover_image?.[0]];
    const response = await Promise.allSettled(formImages.map((image) => uploadImageToS3(image as unknown as File)));

    // TODO: Handle Error Cases here.
    // TODO: Handle Edge Case here where profile image upload fails.
    const [profileImageUrl, coverImageUrl] = response
      .filter((r) => r.status === "fulfilled" && r.value)
      // @ts-ignore
      .map((r) => r.value);

    const apiPayload = Object.assign(formValuesWithHTTPSAppendedForSocialLinks, {
      profile_image_url: profileImageUrl,
      cover_image_url: coverImageUrl,
    });

    // api payload should not contain profile_image and cover_image
    delete apiPayload.profile_image;
    delete apiPayload.cover_image;

    post(BRAND_ROUTE, apiPayload, {
      processData: handleBrandCreateSuccess,
      processError: handleBrandCreateError,
    });
  };

  const onSubmit = () => {
    if (currentStep === 2) {
      handlePostDataToServer();
      // } else if (currentStep === 0 && !!userDetails?.is_dapper_setup === false) {
      //   setError("root", { message: "Linking Dapper Wallet is mandatory for Brand Sign-Up." });
    } else {
      handleSetFormStep(currentStep + 1);
    }
  };

  return (
    <>
      <Helmet>
        <title>Brand Onboarding - MAGNETIQ</title>
      </Helmet>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <FormProvider {...formMethods}>
        <BrandOnboarding
          currentStep={currentStep}
          loading={loading}
          handleSetFormStep={handleSetFormStep}
          handleFormSubmit={handleSubmit(onSubmit)}
        />
      </FormProvider>
    </>
  );
}

export default BrandOnBoardingPage;
